import React, { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "./auth/supabase";
import { useAuth } from "./auth/AuthContext";
import { Footer } from "./App";

/* ============================================================
   Map backend response to verdict UI
   Backend returns:
     { truth: "true"|"false"|"ambivalent", explanation, source1, source2, category }
   ============================================================ */
const VERDICT_FROM_TRUTH = {
  true: { key: "fact", label: "Fact" },
  false: { key: "not", label: "Not" },
  ambivalent: { key: "mixed", label: "Mixed" },
};

const prettyCategory = (cat) => {
  if (!cat) return "";
  const c = String(cat).toLowerCase();
  switch (c) {
    case "health": return "Health";
    case "science": return "Science";
    case "politics": return "Politics";
    case "economy":
    case "economics": return "Economy";
    case "climate": return "Climate / environment";
    case "technology": return "Technology";
    default: return "Other";
  }
};

/* Static demo data — ticker + featured cards + stats are illustrative */
const TICKER = [
  { v: "fact",  t: "A banana is, botanically, a berry." },
  { v: "not",   t: "Bulls are enraged by the colour red." },
  { v: "fact",  t: "Octopuses have three hearts." },
  { v: "mixed", t: "Cracking your knuckles causes arthritis." },
  { v: "not",   t: "You swallow 8 spiders a year in your sleep." },
  { v: "fact",  t: "Iceland has more sheep than people." },
  { v: "mixed", t: "Carrots improve your eyesight." },
  { v: "not",   t: "Vikings wore horned helmets." },
  { v: "fact",  t: "Bees can recognise human faces." },
  { v: "not",   t: "Humans only use 10% of their brains." },
];

const SUGGESTIONS = [
  "Goldfish have a 3-second memory.",
  "The Great Wall of China is visible from space.",
  "Lightning never strikes the same place twice.",
];

/* ============================================================ */
export default function Home() {
  const { user } = useAuth();
  const [query, setQuery] = useState("");
  const [result, setResult] = useState(null);
  const [submittedClaim, setSubmittedClaim] = useState("");
  const [loading, setLoading] = useState(false);
  const [savedHint, setSavedHint] = useState(""); // "" | "saved" | "error"

  const saveToHistory = async (claim, data) => {
    // Only logged-in users get auto-save. Anonymous visitors see the result
    // exactly as before, but nothing is stored.
    if (!user) return;
    if (!data || !data.truth || data.truth === "error") return;
    try {
      const { error } = await supabase.from("saved_checks").insert({
        user_id: user.id,
        claim,
        truth: data.truth,
        explanation: data.explanation || "",
        source1: data.source1 || "",
        source2: data.source2 || "",
        category: data.category || "other",
      });
      if (error) throw error;
      setSavedHint("saved");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Save to history failed:", err);
      setSavedHint("error");
    }
  };

  const runCheck = async (claim) => {
    const q = (claim ?? query).trim();
    if (!q) return;
    setLoading(true);
    setResult(null);
    setSubmittedClaim(q);
    setSavedHint("");
    try {
      const response = await fetch("/api/check", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query: q }),
      });
      const data = await response.json();
      // The backend returns { error: "..." } (with no `truth`) when it can't
      // find good sources or the claim is too vague to check. Normalise that
      // into our error shape so the UI shows a clear message instead of a
      // blank "RESULT" panel.
      if (!response.ok || data.error || !data.truth) {
        setResult({
          truth: "error",
          explanation:
            data.error ||
            "We couldn\u2019t find enough reliable sources to judge this one. Try rephrasing it as a specific, checkable statement \u2014 e.g. “Honey never spoils” rather than “food is bad”.",
          source1: "",
          source2: "",
        });
        return;
      }
      setResult(data);
      saveToHistory(q, data);
    } catch (err) {
      console.error("Check failed:", err);
      setResult({
        truth: "error",
        explanation:
          "Something went wrong reaching our fact-checking service. Please try again in a moment.",
        source1: "",
        source2: "",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    runCheck();
  };

  const handleChip = (claim) => {
    setQuery(claim);
    runCheck(claim);
  };

  const renderResult = () => {
    if (loading) {
      return (
        <div className="loading-panel" aria-live="polite">
          <div className="result-head">
            <div className="skel pill" />
            <div className="skel pill" style={{ width: 90 }} />
            <div className="confidence skel" style={{ width: 110, height: 14 }} />
          </div>
          <div className="skel line lg" style={{ width: "80%" }} />
          <div className="skel line" />
          <div className="skel line" style={{ width: "92%" }} />
          <div className="skel line" style={{ width: "70%" }} />
        </div>
      );
    }
    if (!result) return null;

    if (result.truth === "error") {
      return (
        <div className="result error" role="alert">
          <div className="result-head">
            <span className="verdict err"><span className="pip" />Error</span>
          </div>
          <p className="result-body" style={{ marginTop: 14 }}>{result.explanation}</p>
        </div>
      );
    }

    const v = VERDICT_FROM_TRUTH[String(result.truth).toLowerCase()] || {
      key: "mixed",
      label: "Result",
    };

    const sources = [result.source1, result.source2].filter(Boolean);

    return (
      <div className="result" aria-live="polite">
        <div className="result-head">
          <span className={`verdict ${v.key}`}>
            <span className="pip" />{v.label}
          </span>
          {result.category && (
            <span className="category-pill">{prettyCategory(result.category)}</span>
          )}
          {savedHint === "saved" && (
            <span className="saved-hint" title="Saved to your history">✓ Saved to history</span>
          )}
          {!user && (
            <span className="saved-hint muted">
              <Link to="/login">Sign in</Link> to save this
            </span>
          )}
        </div>
        <p className="result-claim">&ldquo;{submittedClaim}&rdquo;</p>
        <p className="result-body">{result.explanation}</p>
        {sources.length > 0 && (
          <div className="sources">
            {sources.map((href, i) => (
              <a key={i} href={href} target="_blank" rel="noreferrer" className="source">
                <span className="num">{String(i + 1).padStart(2, "0")}</span>
                Source {i + 1}
              </a>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <main>
      <section className="hero">
        <span className="eyebrow"><span className="dot" /> Checked 2,481 claims this week</span>
        <h1>
            <span className="g">Fact</span>
          <span className="a">Or</span>
          <span className="r">Not</span>
        </h1>
        <p className="hero-sub">
          Paste a quote, a headline, or a claim you saw online. We trace it back to primary sources and give you a clear verdict — green, amber, or red.
        </p>

        <form className="search" onSubmit={handleSubmit} autoComplete="off">
          <div className="search-input-wrap">
            <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={'Enter a claim. e.g. "Honey never spoils."'}
            />
          </div>
          <button className="search-btn" type="submit" disabled={loading}>
            {loading ? "Checking…" : "Check"}
            <span className="kbd">⏎</span>
          </button>
        </form>

        <div className="suggestions">
          {SUGGESTIONS.map((s) => (
            <button key={s} type="button" className="chip" onClick={() => handleChip(s)}>
              <span className="lbl">Try:</span>{s}
            </button>
          ))}
        </div>

        {renderResult()}
      </section>

      <section className="recent" aria-label="Recent checks ticker">
        <div className="recent-inner">
          <div className="recent-label">
            <span className="pulse" aria-hidden="true" />
            <span className="mono-label">Live &middot; Recent checks</span>
          </div>
          <div className="ticker-window">
            <div className="ticker-track">
              {[...TICKER, ...TICKER].map((i, idx) => (
                <span key={idx} className={`ticker-item ${i.v}`}>
                  <span className="pip" />{i.t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="trust">
        <div className="stat"><div className="num">62%</div><div className="lbl">Verified as fact</div></div>
        <div className="stat"><div className="num">21%</div><div className="lbl">Partly true / misleading</div></div>
        <div className="stat"><div className="num">17%</div><div className="lbl">False or fabricated</div></div>
        <div className="stat"><div className="num" style={{ color: "var(--ink)" }}>3.4s</div><div className="lbl">Average check time</div></div>
      </section>

      <section className="how">
        <div className="section-head">
          <h2 className="section-title">How a claim becomes <em>a verdict.</em></h2>
          <p className="section-lede">Every check moves through the same three steps so you can trust the answer — and audit it yourself.</p>
        </div>
        <div className="steps">
          <div className="step">
            <div className="n">01</div>
            <h3>You paste a claim.</h3>
            <p>A quote, a screenshot caption, a stat from a news story. We accept anything you can put in a sentence.</p>
          </div>
          <div className="step">
            <div className="n">02</div>
            <h3>We trace the source.</h3>
            <p>An automated pipeline pulls the most relevant primary documents — papers, datasets, public records — and weighs them against the claim.</p>
          </div>
          <div className="step">
            <div className="n">03</div>
            <h3>You get a verdict.</h3>
            <p>Green, amber, or red, plus the sources behind it. Not convinced? Re-run the check and compare.</p>
          </div>
        </div>
      </section>

      <section className="featured">
        <div className="section-head">
          <h2 className="section-title">Recently <em>settled.</em></h2>
          <p className="section-lede">A few claims from the last 72 hours that readers asked us to look into.</p>
        </div>
        <div className="cards">
          <article className="card">
            <div className="top"><span className="verdict fact"><span className="pip" />Fact</span><span className="mono-label">May 14</span></div>
            <p className="claim">&ldquo;Honey, when properly sealed, can last for thousands of years without spoiling.&rdquo;</p>
            <div className="meta"><span>Science</span><span className="sep">&middot;</span><span>5 sources</span></div>
          </article>
          <article className="card">
            <div className="top"><span className="verdict mixed"><span className="pip" />Mixed</span><span className="mono-label">May 13</span></div>
            <p className="claim">&ldquo;We use only 10% of our brains at any given time.&rdquo;</p>
            <div className="meta"><span>Health</span><span className="sep">&middot;</span><span>7 sources</span></div>
          </article>
          <article className="card">
            <div className="top"><span className="verdict not"><span className="pip" />Not</span><span className="mono-label">May 12</span></div>
            <p className="claim">&ldquo;The Great Wall of China is the only man-made object visible from the moon.&rdquo;</p>
            <div className="meta"><span>Science</span><span className="sep">&middot;</span><span>9 sources</span></div>
          </article>
        </div>
      </section>

      <section className="cta">
        <div className="cta-inner">
          <h2>Stop sharing things you&rsquo;re <em>not sure</em> about.</h2>
          <div className="cta-actions">
            <Link to="/about" className="btn btn-line">How it works</Link>
            <Link to="/login" className="btn btn-green">Create an account →</Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
